import React, { useState, useRef, useEffect } from 'react';
import type { ConversacionSummary } from '../types/api.types';

interface ConversationSidebarProps {
  conversations: ConversacionSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onRename: (id: string, titulo: string) => void;
  onDelete: (id: string) => void;
  userEmail: string | null;
  isLoading: boolean;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return date.toLocaleDateString('es-CO', { weekday: 'short' });
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit' });
}

const ConversationItem: React.FC<{
  conv: ConversacionSummary;
  isActive: boolean;
  onSelect: () => void;
  onRename: (titulo: string) => void;
  onDelete: () => void;
}> = ({ conv, isActive, onSelect, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(conv.titulo);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(conv.titulo);
  }, [conv.titulo]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commitRename = () => {
    setEditing(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== conv.titulo) {
      onRename(trimmed);
    } else {
      setEditValue(conv.titulo);
    }
  };

  return (
    <div
      className={`group relative flex items-start gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-colors duration-150
        ${isActive ? 'bg-[#E8EFFE] text-[#0943B5]' : 'hover:bg-gray-100 text-gray-700'}`}
      onClick={() => { if (!editing) onSelect(); }}
    >
      <svg
        className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isActive ? 'text-[#0943B5]' : 'text-gray-400'}`}
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>

      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={e => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') { setEditing(false); setEditValue(conv.titulo); }
            }}
            onClick={e => e.stopPropagation()}
            className="w-full text-xs bg-white border border-[#0943B5]/40 rounded px-1.5 py-0.5 outline-none"
          />
        ) : (
          <p
            className="text-xs font-medium truncate leading-5"
            onDoubleClick={e => { e.stopPropagation(); setEditing(true); }}
          >
            {conv.titulo}
          </p>
        )}
        <p className={`text-[10px] mt-0.5 ${isActive ? 'text-[#0943B5]/60' : 'text-gray-400'}`}>
          {conv.total_mensajes} mens. · {formatDate(conv.updated_at)}
        </p>
      </div>

      <button
        onClick={e => { e.stopPropagation(); onDelete(); }}
        className="opacity-0 group-hover:opacity-100 flex-shrink-0 mt-0.5 text-gray-400 hover:text-red-500 transition-all duration-150"
        title="Eliminar conversación"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>
  );
};

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  activeId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  userEmail,
  isLoading,
}) => {
  return (
    <aside className="w-64 flex-shrink-0 bg-gray-50 border-r border-gray-200 flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200">
        <button
          onClick={onCreate}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-[#0943B5] hover:bg-[#072d8a] text-white text-xs font-medium rounded-xl transition-colors duration-200"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nueva conversación
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {isLoading && conversations.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-[#0943B5] rounded-full animate-spin" />
          </div>
        )}
        {!isLoading && conversations.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-8 px-2">
            No hay conversaciones.<br />Crea una para comenzar.
          </p>
        )}
        {conversations.map(conv => (
          <ConversationItem
            key={conv.id}
            conv={conv}
            isActive={conv.id === activeId}
            onSelect={() => onSelect(conv.id)}
            onRename={titulo => onRename(conv.id, titulo)}
            onDelete={() => onDelete(conv.id)}
          />
        ))}
      </div>

      {userEmail && (
        <div className="px-4 py-3 border-t border-gray-200">
          <p className="text-[10px] text-gray-400 truncate" title={userEmail}>
            {userEmail}
          </p>
        </div>
      )}
    </aside>
  );
};
